import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Digital Marketing",
  "Advertising",
  "Online Marketing",
  "Online Advertising",
  "Hospitality Services",
  "IT Consulting",
  "Other",
];

const initial = { name: "", email: "", phone: "", service: "", message: "" };

const serviceFromUrl = (search) => {
  const svc = new URLSearchParams(search).get("service");
  return svc && SERVICES.includes(svc) ? svc : "";
};

export const Contact = () => {
  const location = useLocation();
  const [form, setForm] = useState(() => ({
    ...initial,
    service: serviceFromUrl(typeof window !== "undefined" ? window.location.search : ""),
  }));

  useEffect(() => {
    const svc = serviceFromUrl(location.search);
    if (svc) setForm((f) => ({ ...f, service: svc }));
  }, [location.search]);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you — our team will be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-white/20 py-4 text-lg text-quantum-text placeholder:text-quantum-muted/60 focus:outline-none focus:border-quantum-gold transition-colors duration-300";

  return (
    <section id="contact" className="bg-quantum-bg2 border-t border-white/10" data-testid="contact-section">
      <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 lg:py-40">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 lg:gap-24">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-quantum-gold mb-6">Get in touch</p>
          <h2 className="font-serif-q text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-light text-quantum-text mb-8">
            Let&apos;s build something exceptional.
          </h2>
          <p className="text-lg text-quantum-muted leading-relaxed mb-10 max-w-md">
            Tell us what you need. Whether it&apos;s a campaign, an IT project or a full
            partnership — we&apos;ll respond within one business day.
          </p>
          <div className="space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-quantum-muted mb-1">Email</p>
              <a href="mailto:info@qauntuminternational.com" className="text-quantum-text hover:text-quantum-gold transition-colors" data-testid="contact-email-link">
                info@qauntuminternational.com
              </a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-quantum-muted mb-1">Landline</p>
              <a href="tel:+97148342593" className="text-quantum-text hover:text-quantum-gold transition-colors" data-testid="contact-phone-link">
                +971 4 834 2593
              </a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-quantum-muted mb-1">Office</p>
              <p className="text-quantum-text leading-relaxed">
                107, Churchill Tower<br />
                Business Bay, Dubai<br />
                United Arab Emirates
              </p>
              <a
                href="https://maps.app.goo.gl/V4j8bWgRFiU3doxUA?g_st=ic"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-link"
                className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-quantum-gold hover:text-white transition-colors"
              >
                Get directions →
              </a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-quantum-muted mb-1">Office hours</p>
              <p className="text-quantum-text" data-testid="contact-hours">
                Monday – Friday · 10.00 to 5.00
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <input
                data-testid="contact-name"
                className={inputCls}
                placeholder="Full name *"
                value={form.name}
                onChange={update("name")}
              />
              <input
                data-testid="contact-phone"
                className={inputCls}
                placeholder="Phone *"
                value={form.phone}
                onChange={update("phone")}
              />
            </div>
            <input
              data-testid="contact-email"
              type="email"
              className={inputCls}
              placeholder="Email address *"
              value={form.email}
              onChange={update("email")}
            />
            <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger
                data-testid="contact-service"
                className="w-full bg-transparent border-0 border-b border-white/20 rounded-none py-6 px-0 text-lg text-quantum-text focus:border-quantum-gold focus:ring-0 [&>span]:text-quantum-text data-[placeholder]:[&>span]:text-quantum-muted/60"
              >
                <SelectValue placeholder="Service of interest *" />
              </SelectTrigger>
              <SelectContent className="bg-quantum-bg3 border-white/10 text-quantum-text">
                {SERVICES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`service-option-${s}`} className="focus:bg-white/10 focus:text-quantum-gold">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <textarea
              data-testid="contact-message"
              rows={3}
              className={`${inputCls} resize-none`}
              placeholder="Tell us about your project"
              value={form.message}
              onChange={update("message")}
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-quantum-gold text-black px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-white transition-colors duration-300 active:scale-95 disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : null}
              {loading ? "Sending" : "Send enquiry"}
              {!loading && (
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              )}
            </button>
          </form>
        </Reveal>
        </div>
      </div>
      <div className="w-full h-[420px] md:h-[560px] lg:h-[640px] border-t border-white/10" data-testid="contact-map">
        <iframe
          title="Qauntum International — office location"
          src="https://maps.google.com/maps?cid=3635578197169249756&output=embed&z=17"
          width="100%"
          height="100%"
          style={{ border: 0, filter: "grayscale(50%) contrast(1.05)" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
    </section>
  );
};
